// CSVImportDropzone — US-127.
//
// Reads the dropped/picked CSV client-side for the preview (first 6 rows
// + URL-column detection + dedupe count), then uploads the file to R2 via
// the presigned PUT URL minted by `POST /campaigns/draft/csv-import/sign-upload`.
// Calls `POST /campaigns/draft/csv-import/complete` once the upload settles
// to mark the row `parsed`.
//
// The `campaignId` prop is `'draft'` while the campaign row doesn't exist
// yet (wizard creates the campaign AFTER the CSV upload completes — the
// returned `csvImportId` is then attached to the create payload).

import { useRef, useState, type ChangeEvent } from 'react';

import {
  useCsvImportComplete,
  useCsvImportSignUpload,
  type CsvImportRow,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Button, Card, Spinner, toast } from '@barel/ui';

export interface CsvParsedPreview {
  headers: string[];
  rows: string[][];
  urlCol: string | null;
  total: number;
  uniqueLeads: number;
  duplicates: number;
}

export interface CSVImportResult {
  csvImportId: string;
  filename: string;
  preview: CsvParsedPreview;
  csvImport: CsvImportRow;
}

export interface CSVImportDropzoneProps {
  /** May be `'draft'` while creating a brand new campaign. */
  campaignId: string;
  /** Result echoed up so the wizard can attach the id + show the summary. */
  onImported: (result: CSVImportResult) => void;
  /** Called when the user clicks "Retirer". */
  onClear: () => void;
  /** Restored state from a prior call — show the preview without re-uploading. */
  initial?: CSVImportResult | null;
}

function parseCsv(text: string): CsvParsedPreview {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const headers = (lines[0] ?? '').split(',').map((h) => h.trim());
  const previewRows = lines.slice(1, 6).map((l) => l.split(',').map((c) => c.trim()));
  const urlColIdx = headers.findIndex((h) => /url|linkedin|profile/i.test(h));
  const total = lines.length - 1;
  const allUrls = lines
    .slice(1)
    .map((l) => (l.split(',')[urlColIdx >= 0 ? urlColIdx : 0] ?? '').trim())
    .filter(Boolean);
  const dedup = new Set(allUrls);
  const duplicates = allUrls.length - dedup.size;
  return {
    headers,
    rows: previewRows,
    urlCol: urlColIdx >= 0 ? (headers[urlColIdx] ?? null) : null,
    total: Math.max(0, total),
    uniqueLeads: dedup.size,
    duplicates: Math.max(0, duplicates),
  };
}

export function CSVImportDropzone({
  campaignId,
  onImported,
  onClear,
  initial,
}: CSVImportDropzoneProps) {
  const t = useT('campaigns');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CSVImportResult | null>(initial ?? null);

  const signUpload = useCsvImportSignUpload();
  const completeUpload = useCsvImportComplete();

  const handleFile = async (file: File) => {
    setBusy(true);
    try {
      const text = await file.text();
      const preview = parseCsv(text);

      const presigned = await signUpload.mutateAsync({
        campaignId,
        filename: file.name,
        contentType: file.type || 'text/csv',
      });

      // R2 PUT — best-effort. In tests / dev without R2 the `uploadUrl` may
      // be `null`; skip the PUT entirely so the row still completes.
      if (presigned.uploadUrl) {
        const put = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          headers: { 'content-type': file.type || 'text/csv' },
          body: file,
        }).catch(() => null);
        if (!put || !put.ok) {
          throw new Error('upload_failed');
        }
      }

      const completion = await completeUpload.mutateAsync({
        campaignId,
        csvImportId: presigned.csvImportId,
        totalRows: preview.total,
        uniqueLeads: preview.uniqueLeads,
        duplicates: preview.duplicates,
        urlColumn: preview.urlCol ?? undefined,
      });

      // SPEC-CAMPAIGN-LOOP §6 — an errored import must NOT surface the
      // success toast (the A2 lie). The draft path 422s on a missing
      // upload; this covers the non-draft parse path.
      if (completion.csvImport.status === 'errored') {
        throw new Error(completion.csvImport.errorReason ?? 'csv_errored');
      }

      const next: CSVImportResult = {
        csvImportId: presigned.csvImportId,
        filename: file.name,
        preview,
        csvImport: completion.csvImport,
      };
      setResult(next);
      onImported(next);
      toast.success({ title: t('toast.csvUploaded') });
    } catch {
      toast.error({ title: t('create.csv.errorUpload') });
    } finally {
      setBusy(false);
    }
  };

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    void handleFile(file);
  };

  if (!result) {
    return (
      <Card padding="lg" radius="2xl" data-testid="csv-dropzone">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center gap-2 px-4 py-8 text-center"
          data-testid="csv-dropzone-pick"
          disabled={busy}
        >
          <span aria-hidden="true" className="text-2xl">
            📄
          </span>
          <strong className="text-primary text-sm">{t('create.csv.dropzone')}</strong>
          <span className="text-tertiary text-xs">{t('create.csv.dropzoneSub')}</span>
          {busy ? <Spinner size="sm" /> : null}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onChangeInput}
          className="hidden"
          data-testid="csv-dropzone-input"
        />
      </Card>
    );
  }

  return (
    <Card padding="md" radius="2xl" data-testid="csv-preview">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="text-lg">
          📄
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-primary">{result.filename}</div>
          <div className="text-xs text-tertiary" data-testid="csv-summary">
            {`${t('create.csv.summary', { total: result.preview.total }) as string}`}
            {result.preview.urlCol
              ? ` · ${t('create.csv.summaryUrlCol', { col: result.preview.urlCol }) as string}`
              : ''}
            {result.preview.duplicates > 0
              ? ` · ${t('create.csv.summaryDupes', { dupes: result.preview.duplicates }) as string}`
              : ''}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setResult(null);
            onClear();
          }}
          data-testid="csv-clear"
        >
          {t('create.csv.remove')}
        </Button>
      </div>
    </Card>
  );
}
